/* Step 1: Product selection for document stepper
   Updated: Fetch supplier, transport_method, type, size from DB and store on LineItem for delivery calculation
   Updated: Custom ("Vrije regel") lines now accept price excl. BTW and a BTW
   percentage (0%, 9% or 21%). Product-derived lines carry the product's
   btw_percentage through to the LineItem so totals can be grouped by rate. */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { calculatePriceInclBTW } from '@/lib/btw-calculator';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Plus, Trash2, Package, PenLine } from 'lucide-react';
import type { LineItem, ProductOption } from './types';

interface ProductStepProps {
  items: LineItem[];
  onItemsChange: (items: LineItem[]) => void;
}

const BTW_OPTIONS = [0, 9, 21];

export default function ProductStep({ items, onItemsChange }: ProductStepProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProductOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customDesc, setCustomDesc] = useState('');
  const [customPriceExcl, setCustomPriceExcl] = useState('');
  const [customBtw, setCustomBtw] = useState<number>(9);
  const [customQty, setCustomQty] = useState('1');

  const searchProducts = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const { data: variants } = await supabase
        .from('product_variants')
        .select(`
          id,
          size,
          type,
          price_tier_1,
          sku,
          active,
          product_id,
          transport_method,
          products (
            id,
            name,
            btw_percentage,
            active,
            supplier
          )
        `)
        .eq('active', true)
        .order('size');

      const { data: simpleProducts } = await supabase
        .from('products')
        .select('id, name, simple_price, simple_sku, btw_percentage, is_simple_product, active')
        .eq('is_simple_product', true)
        .eq('active', true);

      const results: ProductOption[] = [];
      const lowerQuery = query.toLowerCase();

      if (variants) {
        for (const v of variants) {
          const product = v.products as any;
          if (!product?.active) continue;
          const fullName = `${product.name} - ${v.size} (${v.type})`;
          if (fullName.toLowerCase().includes(lowerQuery) || (v.sku && v.sku.toLowerCase().includes(lowerQuery))) {
            const btwPct = Number(product.btw_percentage ?? 9);
            results.push({
              id: product.id,
              variant_id: v.id,
              name: product.name,
              variant_label: `${v.size} (${v.type})`,
              price: calculatePriceInclBTW(v.price_tier_1, btwPct),
              btw_percentage: btwPct,
              sku: v.sku || '',
              is_simple: false,
              variant_size: v.size || '',
              supplier: product.supplier || '',
              transport_method: v.transport_method || '',
              variant_type: v.type || '',
            });
          }
        }
      }

      if (simpleProducts) {
        for (const p of simpleProducts) {
          const fullName = p.name;
          if (fullName.toLowerCase().includes(lowerQuery) || (p.simple_sku && p.simple_sku.toLowerCase().includes(lowerQuery))) {
            const btwPct = Number(p.btw_percentage ?? 9);
            results.push({
              id: p.id,
              name: p.name,
              variant_label: '',
              price: calculatePriceInclBTW(p.simple_price || 0, btwPct),
              btw_percentage: btwPct,
              sku: p.simple_sku || '',
              is_simple: true,
            });
          }
        }
      }

      setSearchResults(results.slice(0, 20));
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchProducts(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchProducts]);

  const addProduct = (option: ProductOption) => {
    const existing = items.find(
      (i) =>
        !i.is_custom &&
        i.product_id === option.id &&
        i.variant_id === option.variant_id
    );

    if (existing) {
      const updated = items.map((i) =>
        i.id === existing.id
          ? { ...i, quantity: i.quantity + 1, total_price: (i.quantity + 1) * i.unit_price }
          : i
      );
      onItemsChange(updated);
    } else {
      const desc = option.variant_label
        ? `${option.name} - ${option.variant_label}`
        : option.name;

      const newItem: LineItem = {
        id: crypto.randomUUID(),
        product_id: option.id,
        variant_id: option.variant_id,
        description: desc,
        quantity: 1,
        unit_price: option.price,
        total_price: option.price,
        is_custom: false,
        btw_percentage: option.btw_percentage,
        variant_size: option.variant_size,
        supplier: option.supplier,
        transport_method: option.transport_method,
        variant_type: option.variant_type,
      };
      onItemsChange([...items, newItem]);
    }

    setSearchQuery('');
    setSearchResults([]);
  };

  const addCustomItem = () => {
    if (!customDesc || !customPriceExcl) return;
    const priceExcl = parseFloat(customPriceExcl);
    const qty = parseInt(customQty) || 1;
    if (isNaN(priceExcl)) return;

    const priceIncl = calculatePriceInclBTW(priceExcl, customBtw);

    const newItem: LineItem = {
      id: crypto.randomUUID(),
      description: customDesc,
      quantity: qty,
      unit_price: priceIncl,
      total_price: priceIncl * qty,
      is_custom: true,
      btw_percentage: customBtw,
    };
    onItemsChange([...items, newItem]);
    setCustomDesc('');
    setCustomPriceExcl('');
    setCustomBtw(9);
    setCustomQty('1');
    setShowCustomForm(false);
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) return;
    const updated = items.map((i) =>
      i.id === id ? { ...i, quantity, total_price: quantity * i.unit_price } : i
    );
    onItemsChange(updated);
  };

  const updatePrice = (id: string, price: number) => {
    const updated = items.map((i) =>
      i.id === id ? { ...i, unit_price: price, total_price: i.quantity * price } : i
    );
    onItemsChange(updated);
  };

  const updateCustomBtw = (id: string, btw: number) => {
    const updated = items.map((i) => {
      if (i.id !== id || !i.is_custom) return i;
      const priceExcl = i.unit_price / (1 + i.btw_percentage / 100);
      const newPriceIncl = calculatePriceInclBTW(priceExcl, btw);
      return {
        ...i,
        btw_percentage: btw,
        unit_price: newPriceIncl,
        total_price: newPriceIncl * i.quantity,
      };
    });
    onItemsChange(updated);
  };

  const removeItem = (id: string) => {
    onItemsChange(items.filter((i) => i.id !== id));
  };

  const subtotal = items.reduce((sum, i) => sum + i.total_price, 0);
  const customPricePreview =
    customPriceExcl && !isNaN(parseFloat(customPriceExcl))
      ? calculatePriceInclBTW(parseFloat(customPriceExcl), customBtw)
      : 0;

  return (
    <div className="space-y-6">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Zoek op productnaam of SKU..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-jv-primary" />
          </div>
        )}

        {searchResults.length > 0 && (
          <Card className="absolute z-50 w-full mt-1 shadow-lg border max-h-64 overflow-auto">
            <CardContent className="p-0">
              {searchResults.map((option) => (
                <button
                  key={`${option.id}-${option.variant_id || 'simple'}`}
                  onClick={() => addProduct(option)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0 transition-colors"
                >
                  <Package className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {option.name}
                      {option.variant_label && (
                        <span className="text-gray-500"> - {option.variant_label}</span>
                      )}
                    </p>
                    {option.sku && (
                      <p className="text-xs text-gray-400">SKU: {option.sku}</p>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-jv-primary whitespace-nowrap">
                    &euro;{option.price.toFixed(2)}
                    <span className="ml-1 text-[10px] text-gray-400 font-normal">
                      ({option.btw_percentage}%)
                    </span>
                  </span>
                </button>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowCustomForm(!showCustomForm)}
          className="text-xs"
        >
          <PenLine className="w-3.5 h-3.5 mr-1.5" />
          Vrije regel toevoegen
        </Button>
      </div>

      {showCustomForm && (
        <Card className="border-dashed border-2">
          <CardContent className="p-4">
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-4">
                <label className="text-xs font-medium text-gray-500 mb-1 block">Omschrijving</label>
                <Input
                  placeholder="Bijv. Advieskosten"
                  value={customDesc}
                  onChange={(e) => setCustomDesc(e.target.value)}
                  className="text-sm"
                />
              </div>
              <div className="col-span-1">
                <label className="text-xs font-medium text-gray-500 mb-1 block">Aantal</label>
                <Input
                  type="number"
                  min="1"
                  value={customQty}
                  onChange={(e) => setCustomQty(e.target.value)}
                  className="text-sm"
                />
              </div>
              <div className="col-span-3">
                <label className="text-xs font-medium text-gray-500 mb-1 block">Prijs excl. BTW</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={customPriceExcl}
                  onChange={(e) => setCustomPriceExcl(e.target.value)}
                  className="text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-500 mb-1 block">BTW %</label>
                <Select
                  value={String(customBtw)}
                  onValueChange={(v) => setCustomBtw(Number(v))}
                >
                  <SelectTrigger className="text-sm h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BTW_OPTIONS.map((pct) => (
                      <SelectItem key={pct} value={String(pct)}>
                        {pct}%
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex items-end">
                <Button
                  onClick={addCustomItem}
                  size="sm"
                  className="w-full bg-jv-primary hover:bg-jv-primary/90"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {customPriceExcl && (
              <p className="text-[11px] text-gray-500 mt-2">
                Prijs incl. BTW: <span className="font-semibold text-gray-700">&euro;{customPricePreview.toFixed(2)}</span>
                {' '}per stuk
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {items.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Omschrijving</th>
                    <th className="text-center text-xs font-semibold text-gray-600 px-4 py-3 w-20">Aantal</th>
                    <th className="text-right text-xs font-semibold text-gray-600 px-4 py-3 w-32">Prijs/stuk (incl. BTW)</th>
                    <th className="text-center text-xs font-semibold text-gray-600 px-4 py-3 w-24">BTW</th>
                    <th className="text-right text-xs font-semibold text-gray-600 px-4 py-3 w-28">Totaal</th>
                    <th className="w-12 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b last:border-b-0 hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-gray-900">{item.description}</span>
                        {item.is_custom && (
                          <span className="ml-2 text-[10px] bg-[#f5eede] text-[#7a6450] px-1.5 py-0.5 rounded font-medium">
                            Vrij
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                          className="w-20 text-center text-sm mx-auto"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price.toFixed(2)}
                          onChange={(e) => updatePrice(item.id, parseFloat(e.target.value) || 0)}
                          className="w-28 text-right text-sm ml-auto"
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.is_custom ? (
                          <Select
                            value={String(item.btw_percentage)}
                            onValueChange={(v) => updateCustomBtw(item.id, Number(v))}
                          >
                            <SelectTrigger className="text-xs h-8 w-20 mx-auto">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {BTW_OPTIONS.map((pct) => (
                                <SelectItem key={pct} value={String(pct)}>
                                  {pct}%
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-gray-500">{item.btw_percentage}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-semibold text-gray-900">
                          &euro;{item.total_price.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-2 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(item.id)}
                          className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border-t bg-gray-50 px-4 py-4 flex justify-end">
              <div className="text-right">
                <span className="text-sm text-gray-500 mr-4">Subtotaal incl. BTW:</span>
                <span className="text-lg font-bold text-jv-primary">
                  &euro;{subtotal.toFixed(2)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {items.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Zoek en voeg producten toe om te beginnen</p>
        </div>
      )}
    </div>
  );
}
